import { useCallback, useRef } from 'react';

/**
 * Touch events are implicitly captured to the element where the touch
 * started. react-beautiful-dnd's cloning API (required for virtual lists)
 * unmounts the original in-list item the moment a drag lifts, so the browser
 * keeps dispatching touchmove/touchend at the now-detached node and they
 * never bubble to rbd's window-bound listeners - the drag freezes mid-lift
 * and never completes. Per the Touch Events spec, the fix is to listen
 * directly on the touch target and forward events to the window while the
 * node is detached.
 *
 * Returns a ref callback to attach to the drag-handle element. Pass
 * enabled=false (e.g. on desktop) to make it inert.
 */
const useDetachedTouchBridge = (enabled) => {
  const cleanupRef = useRef(null);

  const onTouchStart = useRef((event) => {
    const node = event.currentTarget;

    const forward = (ev) => {
      /** while the node is attached, natural bubbling already delivers the
       * event to rbd's window listeners - only bridge when detached */
      if (node.isConnected || typeof TouchEvent !== 'function') return;
      try {
        const clone = new TouchEvent(ev.type, {
          bubbles: true,
          cancelable: true,
          view: window,
          touches: Array.from(ev.touches),
          targetTouches: Array.from(ev.targetTouches),
          changedTouches: Array.from(ev.changedTouches),
        });
        window.dispatchEvent(clone);
      } catch (err) {
        /** TouchEvent constructor unsupported - nothing more we can do */
      }
    };

    const onEnd = (ev) => {
      forward(ev);
      node.removeEventListener('touchmove', forward);
      node.removeEventListener('touchend', onEnd);
      node.removeEventListener('touchcancel', onEnd);
    };

    /** per-gesture listeners on the node itself: these keep firing even
     * after the node is detached from the document */
    node.addEventListener('touchmove', forward, { passive: false });
    node.addEventListener('touchend', onEnd);
    node.addEventListener('touchcancel', onEnd);
  }).current;

  return useCallback((node) => {
    if (cleanupRef.current) {
      cleanupRef.current();
      cleanupRef.current = null;
    }
    if (node && enabled) {
      node.addEventListener('touchstart', onTouchStart, { passive: true });
      cleanupRef.current = () => node.removeEventListener('touchstart', onTouchStart);
    }
  }, [enabled, onTouchStart]);
};

export default useDetachedTouchBridge;
